import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import "core-js/modules/web.dom-collections.iterator.js";
import T from 'prop-types';
import { forwardRef, useRef, useState } from 'react';
import styled from 'styled-components';
import { createShouldForwardProp } from '../../helpers/createShouldForwardProp';
import { isPercentage } from '../../helpers/regexp';
import { useForkRef } from '../../hooks/useForkRef';
import { useIsomorphicLayoutEffect } from '../../hooks/useIsomorphicLayoutEffect';
import { blackAlpha2, blackAlpha8 } from '../../styles/colors';
import { centerAbsolute } from '../../styles/mixins';
import { Icon } from '../Icon/Icon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ImgContainer = /*#__PURE__*/styled.div.withConfig({
  shouldForwardProp: createShouldForwardProp(['height', 'backgroundColor'])
}).withConfig({
  displayName: "ImgContainer",
  componentId: "rs8ast-0"
})(["position:relative;width:100%;height:", ";background:", ";"], props => props.height, props => props.backgroundColor);
const Img = /*#__PURE__*/styled.img.withConfig({
  shouldForwardProp: createShouldForwardProp(['height'])
}).withConfig({
  displayName: "Img",
  componentId: "rs8ast-1"
})(["width:100%;object-fit:cover;height:", ";opacity:", ";transition:", ";"], props => props.height, props => props.isVisible ? 1 : 0, props => props.shouldTransition ? 'opacity 80ms ease-out' : 'none');
const ImgIcon = /*#__PURE__*/styled(Icon).withConfig({
  displayName: "ImgIcon",
  componentId: "rs8ast-2"
})(["", ";opacity:", ";transition:opacity 80ms ease-out;"], centerAbsolute, props => props.isVisible ? 1 : 0);
const PENDING = 'PENDING';
const RESOLVED = 'RESOLVED';
const Image = /*#__PURE__*/forwardRef(function Image(_ref, forwardedRef) {
  let {
    src,
    alt,
    title,
    height,
    loading = 'lazy',
    backgroundColor = blackAlpha2,
    placeholderGlyph,
    placeholderGlyphColor = blackAlpha8,
    placeholderGlyphSize
  } = _ref,
      restProps = _objectWithoutPropertiesLoose(_ref, ["src", "alt", "title", "height", "loading", "backgroundColor", "placeholderGlyph", "placeholderGlyphColor", "placeholderGlyphSize", "onLoad", "onError"]);

  const [status, setStatus] = useState(PENDING);
  const [shouldTransition, setShouldTransition] = useState(true);
  const imageRef = useRef();
  const handleRef = useForkRef(forwardedRef, imageRef); // If the image is cached we update the status before the first paint and
  // disable the animation.

  useIsomorphicLayoutEffect(() => {
    if (imageRef.current.complete) {
      setStatus(RESOLVED);
      setShouldTransition(false);
    }
  }, [src]);
  const isResolved = status === RESOLVED;
  const imageHeight = isPercentage(height) ? height : `${parseInt(height, 10)}px`;
  const iconSize = placeholderGlyphSize || `${parseInt(height, 10) * 0.5}${isPercentage(height) ? '%' : 'px'}`;
  return /*#__PURE__*/_jsxs(ImgContainer, {
    height: imageHeight,
    backgroundColor: backgroundColor,
    children: [placeholderGlyph && /*#__PURE__*/_jsx(ImgIcon, {
      isVisible: !isResolved,
      glyph: placeholderGlyph,
      primaryColor: placeholderGlyphColor,
      size: iconSize
    }), /*#__PURE__*/_jsx(Img, Object.assign({}, restProps, {
      ref: handleRef,
      loading: loading,
      src: src,
      alt: alt,
      title: title,
      onLoad: () => {
        setStatus(RESOLVED);
      },
      height: imageHeight,
      isVisible: isResolved,
      shouldTransition: shouldTransition
    }))]
  });
});
Image.propTypes = {
  src: T.string.isRequired,
  alt: T.string.isRequired,
  title: T.string,
  loading: T.oneOf(['lazy', 'eager']),

  /** pixels or % */
  height: T.oneOfType([T.string, T.number]).isRequired,
  placeholderGlyph: T.elementType,
  placeholderGlyphColor: T.string,
  placeholderGlyphSize: Icon.propTypes.size,
  backgroundColor: T.string,
  onLoad: T.func,
  onError: T.func
};
export { Image };